import React from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface DropdownProps {
  trigger: React.ReactNode;
  items: Array<{
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    dangerous?: boolean;
    disabled?: boolean;
  }>;
  align?: 'left' | 'right';
}

export function Dropdown({ trigger, items, align = 'right' }: DropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative inline-block">
      <div onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-1">
        {trigger}
        <svg className={clsx('w-4 h-4 transition-transform', isOpen && 'rotate-180')}>
          <ChevronDownIcon />
        </svg>
      </div>

      {isOpen && (
        <div
          className={clsx(
            'absolute z-50 mt-2 w-48 origin-top-right rounded-xl bg-white dark:bg-dark-900 shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none animate-scale-in',
            align === 'right' ? 'right-0' : 'left-0'
          )}
          role="menu"
        >
          <div className="py-1">
            {items.map((item, index) => (
              <button
                key={index}
                onClick={() => { item.onClick(); setIsOpen(false); }}
                disabled={item.disabled}
                className={clsx(
                  'w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors',
                  item.dangerous
                    ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                    : 'text-dark-700 dark:text-dark-300 hover:bg-dark-100 dark:hover:bg-dark-800',
                  item.disabled && 'opacity-50 cursor-not-allowed'
                )}
                role="menuitem"
              >
                {item.icon && <span className="w-5 h-5">{item.icon}</span>}
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}